import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Box, Stack, Typography, useTheme, alpha } from "@mui/material";
import ReportLoader from "../../Reports/Loader";
import { colors, shadows } from "../source/source.styles";
import { Trophy, Zap, TrendingUp, DollarSign } from "lucide-react";
import { getEmployeePerformanceApi } from "../../../api/reports.api";
import socket from "../../../utils/socket";
import dayjs from "dayjs";

const EmployeeMetrics = ({ dateRange }) => {
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;
  const [teamMetrics, setTeamMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Use a ref to track if socket has been initialized
  const socketInitialized = useRef(false);

  // Convert dateRange to stable strings for dependency tracking
  const dateRangeKey = useMemo(() => {
    if (dateRange?.startDate && dateRange?.endDate) {
      return `${dateRange.startDate.getTime()}-${dateRange.endDate.getTime()}`;
    }
    return "";
  }, [dateRange]);

  const fetchMetrics = useCallback(async () => {
    try {
      setLoading(true);

      // Build params with date range if provided
      const params = {};
      if (dateRange?.startDate) {
        params.from = dayjs(dateRange.startDate).toISOString();
      }
      if (dateRange?.endDate) {
        params.to = dayjs(dateRange.endDate).toISOString();
      }

      const response = await getEmployeePerformanceApi(params);

      if (response?.data?.data?.teamMetrics) {
        setTeamMetrics(response.data.data.teamMetrics);
      }
    } catch (error) {
      console.error("Failed to fetch team metrics:", error);
    } finally {
      setLoading(false);
    }
  }, [dateRangeKey]);

  // Fetch data when date range changes
  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  // Setup socket listeners only once
  useEffect(() => {
    if (socketInitialized.current) return;
    socketInitialized.current = true;

    socket.connect();

    // Listen for real-time employee metrics updates - trigger refresh
    const handleMetricsUpdate = () => {
      setRefreshTrigger(prev => prev + 1);
    };

    socket.on("employee:metrics-updated", handleMetricsUpdate);
    socket.on("employee:conversion-updated", handleMetricsUpdate);
    socket.on("lead-status-updated", handleMetricsUpdate);

    return () => {
      socket.off("employee:metrics-updated", handleMetricsUpdate);
      socket.off("employee:conversion-updated", handleMetricsUpdate);
      socket.off("lead-status-updated", handleMetricsUpdate);
    };
  }, []);

  if (loading) {
    return <ReportLoader />;
  }

  const formatRevenue = (amount) => {
    if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(1)}L`;
    }
    return `₹${(amount / 1000).toFixed(0)}K`;
  };

  const metrics = [
    {
      icon: Trophy,
      iconColor: "#F59E0B",
      iconBg: "#FEF3C7",
      label: "TOP PERFORMER",
      value: teamMetrics?.topPerformer?.name || "N/A",
      change: teamMetrics?.topPerformer
        ? `↗ ${teamMetrics.topPerformer.efficiencyScore.toFixed(0)}% efficiency`
        : "No data",
      changeColor: colors.success,
    },
    {
      icon: Zap,
      iconColor: "#3B82F6",
      iconBg: "#DBEAFE",
      label: "AVG. RESPONSE TIME",
      value: teamMetrics?.avgResponseTime !== undefined
        ? `${teamMetrics.avgResponseTime}m`
        : "N/A",
      change: "Team average",
      changeColor: colors.textSecondary,
    },
    {
      icon: TrendingUp,
      iconColor: primaryColor,
      iconBg: alpha(primaryColor, 0.15),
      label: "TEAM CONVERSION RATE",
      value: teamMetrics?.avgConversionRate
        ? `${teamMetrics.avgConversionRate}%`
        : "0%",
      change: `${teamMetrics?.totalConversions || 0} conversions`,
      changeColor: colors.textSecondary,
    },
    {
      icon: DollarSign,
      iconColor: "#10B981",
      iconBg: "#D1FAE5",
      label: "TOTAL TEAM REVENUE",
      value: teamMetrics?.totalRevenue
        ? formatRevenue(teamMetrics.totalRevenue)
        : "₹0",
      change: `${teamMetrics?.totalLeads || 0} leads handled`,
      changeColor: colors.success,
    },
  ];

  return (
    <Stack direction="row" spacing={3}>
      {metrics.map((metric, index) => {
        const IconComponent = metric.icon;
        return (
          <Box
            key={index}
            sx={{
              flex: 1,
              bgcolor: "#FFFFFF",
              borderRadius: 3,
              p: 3,
              border: `1px solid ${colors.borderLight}`,
              boxShadow: shadows.card,
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ width: "100%" }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: colors.textMuted,
                    letterSpacing: 0.5,
                    mb: 1,
                  }}
                >
                  {metric.label}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 24,
                    fontWeight: 700,
                    color: colors.textPrimary,
                    mb: 0.5,
                  }}
                >
                  {metric.value}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: index === 2 ? 500 : 600,
                    color: metric.changeColor,
                  }}
                >
                  {metric.change}
                </Typography>
              </Box>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  bgcolor: metric.iconBg,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <IconComponent size={24} color={metric.iconColor} />
              </Box>
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
};

export default EmployeeMetrics;
